
import { z } from 'zod';

/**
 * Enhanced validation schemas with security considerations
 */
export const secureValidationSchemas = {
  // Email validation with additional security checks
  email: z
    .string()
    .email('Invalid email address')
    .max(254, 'Email address too long') // RFC 5321 limit
    .refine(
      (email) => !email.includes('..'),
      'Email contains invalid characters'
    )
    .refine(
      (email) => !/[<>"]/.test(email),
      'Email contains invalid characters'
    ),

  // Password with strong requirements
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password too long')
    .refine(
      (password) => /[A-Z]/.test(password),
      'Password must contain at least one uppercase letter'
    )
    .refine(
      (password) => /[a-z]/.test(password),
      'Password must contain at least one lowercase letter'
    )
    .refine(
      (password) => /[0-9]/.test(password),
      'Password must contain at least one number'
    )
    .refine(
      (password) => /[^A-Za-z0-9]/.test(password),
      'Password must contain at least one special character'
    ),

  // Secure text input (no HTML, limited length)
  secureText: z
    .string()
    .max(1000, 'Text too long')
    .refine(
      (text) => !/<[^>]*>/g.test(text),
      'HTML tags are not allowed'
    )
    .refine(
      (text) => !/javascript:/gi.test(text),
      'JavaScript URLs are not allowed'
    ),

  // Name validation
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name too long')
    .refine(
      (name) => /^[a-zA-Z\s\-'\.]+$/.test(name),
      'Name contains invalid characters'
    ),

  // Phone number validation
  phone: z
    .string()
    .max(20, 'Phone number too long')
    .refine(
      (phone) => /^[\+]?[1-9][\d\s\-\(\)\.]{7,}$/.test(phone) || phone === '',
      'Invalid phone number format'
    )
    .optional(),

  // URL validation with security checks
  url: z
    .string()
    .url('Invalid URL')
    .refine(
      (url) => /^https?:\/\//.test(url),
      'Only HTTP and HTTPS URLs are allowed'
    )
    .refine(
      (url) => !url.includes('javascript:'),
      'JavaScript URLs are not allowed'
    )
    .optional(),

  // File name validation
  fileName: z
    .string()
    .max(255, 'File name too long')
    .refine(
      (name) => !/[<>:"/\\|?*]/.test(name),
      'File name contains invalid characters'
    )
    .refine(
      (name) => !name.startsWith('.'),
      'File name cannot start with a dot'
    ),
};

/**
 * Validates and sanitizes candidate form data
 */
export const candidateFormValidation = z.object({
  first_name: secureValidationSchemas.name,
  last_name: secureValidationSchemas.name,
  email: secureValidationSchemas.email.optional().or(z.literal('')),
  phone: secureValidationSchemas.phone.or(z.literal('')),
  source: secureValidationSchemas.secureText.optional().or(z.literal('')),
  status: z.string().optional(),
});

/**
 * Validates email content with security checks
 */
export const emailContentValidation = z.object({
  subject: z.string().max(998, 'Subject line too long'), // RFC 5322 limit
  body: z.string().max(100000, 'Email body too long'),
  to: z.array(secureValidationSchemas.email),
  cc: z.array(secureValidationSchemas.email).optional(),
  bcc: z.array(secureValidationSchemas.email).optional(),
});
